export const strings = {
  app: {
    name: "Zitadel Bridge",
    subtitle: "Bridge console",
    documentTitle: "Zitadel Bridge — Bridge console",
    footer: "Zitadel Bridge · bridge console",
  },
  endpoints: {
    status: "/api/bridge/status",
    wizardState: "/api/bridge/wizard/state",
    wizardHealth: "/api/bridge/wizard/health",
    preview: "/api/bridge/wizard/preview",
    apply: "/api/bridge/wizard/apply",
    complete: "/api/bridge/wizard/complete",
  },
  steps: {
    review: {
      title: "Review & apply",
      description: "Dry-run preview, then apply reconciliation from config.json.",
    },
    secrets: {
      title: "Credentials",
      description: "One-time client secrets; export before Finish.",
    },
    done: {
      title: "Summary",
      description: "Shown after Apply and Finish.",
    },
  },
  nav: {
    back: "Back",
    continue: "Continue",
    live: "Live dashboard",
    observability: "Observability",
    bootstrap: "Bootstrap",
  },
  live: {
    title: "Live activity",
    description:
      "Real-time SignalR stream of logical bridge flows in both directions. Counters and log lines are held in this instance only and reset when the process restarts.",
    openWizard: "Open setup wizard",
    stats: {
      total: "Total events",
      zitadelToBus: "Zitadel → bus",
      busToZitadel: "Bus → Zitadel",
      topTypes: "Top message types",
      instanceNote: "Per-instance — not persisted across restarts.",
    },
    logTitle: "Event log",
    logEmpty: "No events yet. Zitadel ingest and bus commands will appear here as they are processed.",
    direction: {
      zitadelToBus: "Zitadel→Bus",
      busToZitadel: "Bus→Zitadel",
    },
    outcome: {
      ok: "OK",
      skip: "SKIP",
      fail: "ERR",
    },
    connection: {
      connecting: "Connecting…",
      connected: "Live",
      reconnecting: "Reconnecting…",
      disconnected: "Offline",
    },
  },
  verdict: {
    applyRequiredHeadline: "Apply required",
    systemOkHeadline: "No apply needed",
    firstInstallSummary: "The bridge has never completed its first bootstrap.",
    firstInstallDetail: "Run Apply on this page to provision Zitadel resources and record wizard state.",
    configUpdateSummary: "config.json changed since the last apply.",
    configUpdateDetail: "Run Apply to reconcile Zitadel with the mounted configuration.",
    systemOkSummary:
      "Bootstrap is complete and config.json matches the last apply. Zitadel is in sync — you do not need to run Apply.",
    wizardModeNote:
      "WizardRequired mode is on (automatic startup bootstrap is disabled). Use this wizard when you change config.json.",
    lastApplied: (when: string) => `Last apply: ${when}.`,
    lastAppliedWithOperator: (when: string, who: string) => `Last apply: ${when} (${who}).`,
  },
  toast: {
    previewFailed: "Preview failed.",
    applyCompleted: "Apply completed.",
    applyFailed: "Apply failed.",
    readinessFailed: "Resolve readiness checks before applying.",
    credentialsCopiedJson: "Credentials copied (JSON).",
    credentialsCopiedEnv: "Credentials copied (.env).",
    clipboardFailed: "Could not access the clipboard.",
    finalizeSigningIn: "Wizard finalized — signing you in with Zitadel…",
    finalizeFailed: "Failed to finalize the wizard.",
    copyFailed: "Copy failed — copy manually.",
    stateCopied: "Wizard state copied.",
    statusCopied: "Bridge status copied.",
    applyResultsCopied: "Apply results copied.",
  },
  scenarios: {
    firstInstall: {
      title: "Initial setup required",
      description:
        "The bridge has not completed its first bootstrap yet. Confirm readiness below, then continue to review and apply.",
      cta: "Continue to review",
    },
    configUpdate: {
      title: "Configuration update available",
      description:
        "Mounted config.json differs from the last applied fingerprint. Review the preview, then apply to reconcile.",
      cta: "Review changes",
    },
    ready: {
      title: "No bootstrap work pending",
      description: "Zitadel matches the last applied configuration. You can re-run a preview on Review.",
      cta: "Open review",
    },
    wizardRequired: {
      title: "Wizard mode active",
      description: "Automatic startup bootstrap is disabled. Use the wizard when you need to reconcile Zitadel.",
    },
  },
  pages: {
    overview: {
      title: "Bridge bootstrap",
      description:
        "Reconciles organization, project, roles, OIDC applications, and machine users from mounted config.json. Idempotent.",
      continueReview: "Review changes",
      upToDate: "Open review",
      openReviewOptional: "Open review (optional)",
      systemOkHint:
        "Bootstrap is complete. The summary step appears only after you run Apply and Finish in this wizard session.",
      statePanel: "Live state",
      readinessTitle: "Readiness",
      readinessDescription: "Management API credentials and Zitadel connectivity before preview or apply.",
      readinessReady: "Ready",
      readinessNotReady: "Not ready",
      readinessLoading: "Checking readiness…",
      readinessHelpTitle: "How to fix readiness",
      scopeTitle: "Apply scope",
      scopeItems: [
        "Organization and project from config.json",
        "Project roles and IAM bindings",
        "Configured OIDC applications and machine users",
        "Bridge service account (IAM_OWNER)",
        "Wizard OIDC client (PKCE)",
      ],
    },
    review: {
      title: "Review & apply",
      description:
        "Runs a dry-run preview against Zitadel, then apply to reconcile from mounted config.json. The preview lists planned mutations; an empty list does not always mean apply can be skipped.",
      descriptionUpToDate:
        "No bootstrap work is pending. Re-run preview to verify Zitadel still matches config.json, or return to Overview.",
      descriptionSystemOk:
        "Bootstrap is already complete. Preview is informational; Apply is optional unless you changed config.json.",
      descriptionAfterApply:
        "Apply completed. Continue to Credentials to export secrets, then Finish to enable strict authentication.",
      bannerFirstInstall: "Initial setup",
      bannerConfigUpdate: "Configuration update",
      bannerApplied: "Bootstrap applied",
      bannerSystemOk: "Bootstrap complete",
      statusApplyRequired: "Apply required",
      statusNoApplyNeeded: "No apply needed",
      statusUpToDate: "Up to date",
      statusApplied: "Applied",
      systemOkOnReview: (lastApplied: string, previewMutations: number) =>
        lastApplied !== "—"
          ? `Last apply ${lastApplied}. Preview shows ${previewMutations} planned mutation${previewMutations === 1 ? "" : "s"} — Apply is not required unless you changed config.json.`
          : `Preview shows ${previewMutations} planned mutation${previewMutations === 1 ? "" : "s"}. Bootstrap is complete — Apply is not required unless you changed config.json.`,
      firstInstallWithChanges:
        "The preview listed changes below. Apply provisions the bridge in Zitadel and records wizard state.",
      firstInstallNoChanges:
        "The preview listed no mutations. That is normal when Zitadel already matches config.json or resources already exist. Apply is still required to complete the initial bootstrap.",
      configUpdateWithChanges:
        "The preview listed changes from your updated config.json. Review them, then apply to reconcile.",
      configUpdateNoChanges:
        "The preview listed no mutations. Apply will still mark the current config fingerprint as reconciled.",
      upToDateDescription:
        "No bootstrap work is pending. Re-run preview anytime to verify Zitadel matches config.json.",
      appliedDescription:
        "Bootstrap apply finished. Export credentials on the next step before enabling strict authentication.",
      rerun: "Re-run preview",
      running: "Running preview…",
      previewSection: "Preview result",
      pendingChanges: "Planned mutations",
      pendingBadge: (n: number) => `${n} mutation${n === 1 ? "" : "s"}`,
      unchangedBadge: (n: number) => `${n} unchanged`,
      allOperations: "Full operation log",
      allOperationsBadge: (n: number) => `${n} total`,
      applications: "Credentials touched by preview",
      allApplications: "All provisioned credentials",
      noApplications: "No application credentials in the preview result.",
      noPendingChanges: "No mutations in preview",
      noPendingHintFirstInstall:
        "The dry-run did not plan any changes. Apply is still required to complete the initial bootstrap and record wizard state.",
      noPendingHintConfigUpdate:
        "The dry-run did not plan any changes. Apply will mark the current config fingerprint as reconciled.",
      noPendingHintUpToDate: "Preview matches the last applied configuration. Re-run preview after config changes.",
      noPendingHintSystemOk:
        "No mutations planned. Bootstrap is already complete — you can return to Overview. Run Apply only if you intentionally want to reconcile again.",
      applyChanges: "Apply reconciliation",
      applyOptional: "Apply again (optional)",
      applyFirstInstall: "Complete initial bootstrap",
      applying: "Applying…",
      alreadyApplied: "Apply completed",
      applyHint: "Apply performs the same reconciliation as preview, with real mutations.",
      applyHintNoPending:
        "Apply provisions bridge state and credentials even when the preview listed no mutations.",
      applyHintUpToDate: "Apply is only needed when bootstrap work is pending.",
      applyHintSystemOk:
        "Optional — only needed after config.json changes or to run another reconciliation. Idempotent; safe to skip when Zitadel is already in sync.",
      applyHintAfterApply: "Continue to Credentials to export secrets from this apply run.",
      logsEmpty: "Apply logs appear here while reconciliation runs.",
      nextLabel: "Continue to credentials",
      nextDisabledPreview: "Wait for the preview to finish.",
      nextDisabledApply: "Run apply before continuing to credentials.",
    },
    secrets: {
      title: "Credentials",
      description:
        "Export client secrets now and store them in your external secret manager (Vault, Azure Key Vault, etc.). The bridge host is not a long-term secret store.",
      storageGuidanceTitle: "Store secrets externally",
      storageGuidanceBody:
        "Copy the values below into your deployment secret store before clicking Finish. Client secrets shown here are one-time; the bridge service-account PAT remains on the host data directory for subsequent management calls.",
      revealAll: "Reveal all",
      hideAll: "Hide all",
      copyJson: "Copy JSON",
      copyEnv: "Copy .env",
      noResult: "No apply result in this session. Run apply on Review & apply first.",
      confirmLabel: "Credentials stored in external secret manager",
      confirmHint: "Service-account PAT remains on disk; client secrets shown here are one-time.",
      finishTitle: "Finish wizard",
      finishBody:
        "Enables strict authentication (AllowAnonymous=false in the host auth overlay) and redirects you to Zitadel sign-in. Do this only after secrets are stored externally.",
      finishButton: "Finish and sign in",
      finalizing: "Finalizing…",
      nextDisabledApply: "Apply changes on the review step first.",
      nextDisabledConfirm: "Confirm secret storage to continue.",
    },
    done: {
      title: "Wizard complete",
      badge: "Bootstrap applied",
      summaryWithCounts: (creds: number, ops: number, target: string) =>
        `Reconciled ${creds} credentials, ${ops} operations against ${target}.`,
      summary: (target: string) => `Reconciliation complete against ${target}.`,
      restartBootstrap: "Back to review",
      authFinalizedTitle: "Strict authentication enabled",
      authFinalizedBody:
        "Finish completed and Zitadel sign-in succeeded. AllowAnonymous=false is pinned in the host auth overlay.",
      authPendingTitle: "Strict authentication not enabled yet",
      authPendingBody:
        "If you have not clicked Finish on Credentials yet, the wizard API may still allow anonymous access. Finish stores credentials externally and enables sign-in.",
      patNote:
        "Service account PAT is on the host data directory and drives subsequent management API calls instead of the bootstrap PAT.",
      operatorTitle: "Operator actions",
      operatorItems: [
        "Verify GET /api/bridge/status reflects expected org/project",
        "Confirm auth-overrides.json on the host if strict auth was enabled",
        "Restart bridge container after credential rotation if required",
      ],
      restartExample: "docker compose restart exampleonline-identity-zitadel-bridge",
    },
  },
  flags: {
    firstStartPending: "First-Start: Pending",
    firstStartCompleted: "First-Start: Completed",
    updatesPending: "Updates: Pending",
    updatesClean: "Updates: Clean",
    modeWizardRequired: "Mode: WizardRequired",
    modeAutomatic: "Mode: Automatic",
    authAnonymous: "Anonymous",
    authAuthenticated: "Authenticated",
    phase: "Phase",
    pendingReason: "Pending reason",
    lastApplied: "Last applied",
  },
  table: {
    timestamp: "Time",
    stage: "Stage",
    outcome: "Outcome",
    message: "Message",
    key: "Key",
    name: "Name",
    type: "Type",
    credentials: "Credentials",
    clientId: "Client ID",
    secret: "Secret",
    noOperations: "No operations.",
    filterOutcome: "Filter outcome",
    allOutcomes: "All",
  },
} as const;
